import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { Paperclip, ArrowUp, Loader2 } from 'lucide-react';
import ModelDropdown, { Model, models } from '@/components/ModelDropdown';
import CreditDisplay from '@/components/CreditDisplay';
import { getCredits } from '@/services/credits';

interface ChatInputProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  onSubmit: (e?: React.FormEvent) => void;
  isLoading: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  selectedModel?: Model;
  onModelChange?: (model: Model) => void;
  userCreditsRemaining?: number;
  userTier?: 'free' | 'pro' | 'ultimate';
  modelColumns?: 2 | 3;
  contextIndicator?: {
    icon?: React.ReactNode;
    text: string;
    subtext?: string;
  };
}

export default function ChatInput({
  prompt,
  onPromptChange,
  onSubmit,
  isLoading,
  placeholder = 'What SVG do you want to create?',
  className = '',
  disabled = false,
  selectedModel: propSelectedModel,
  onModelChange,
  userCreditsRemaining,
  userTier = 'free',
  contextIndicator,
}: ChatInputProps) {
  const [internalSelectedModel, setInternalSelectedModel] = useState<Model>(models[0]);
  const selectedModel = propSelectedModel || internalSelectedModel;
  const setSelectedModel = onModelChange || setInternalSelectedModel;
  const [isMultiline, setIsMultiline] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [responsivePlaceholder, setResponsivePlaceholder] = useState(placeholder);

  // Handle responsive placeholder text
  useEffect(() => {
    const updatePlaceholder = () => {
      if (window.innerWidth < 640) {
        setResponsivePlaceholder('Create an SVG');
      } else {
        setResponsivePlaceholder(placeholder);
      }
    };

    updatePlaceholder();
    window.addEventListener('resize', updatePlaceholder);
    return () => window.removeEventListener('resize', updatePlaceholder);
  }, [placeholder]);

  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;

      // Save current scroll position
      const scrollTop = textarea.scrollTop;

      // Reset height to recalculate
      const newHeight = Math.min(textarea.scrollHeight, 200);
      textarea.style.height = '26px'; // Set to min-height first
      if (prompt.trim()) {
        // Calculate new height
        textarea.style.height = `${newHeight}px`;

        // Restore scroll position
        textarea.scrollTop = scrollTop;
      }

      // If prompt is empty, reset to single line
      if (!prompt.trim()) {
        setIsMultiline(false);
      }
      // Once we go multiline, stay multiline (unless cleared)
      else if (!isMultiline && newHeight > 45) {
        setIsMultiline(true);
      }
    }
  }, [prompt, isMultiline]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!prompt.trim() || isLoading || disabled) return;
      onSubmit();
    }
  };

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Don't focus if clicking on a button or input
    const target = e.target as HTMLElement;
    if (
      target.tagName !== 'BUTTON' &&
      target.tagName !== 'INPUT' &&
      target.tagName !== 'TEXTAREA' &&
      !target.closest('button') &&
      textareaRef.current
    ) {
      textareaRef.current.focus();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        if (!prompt.trim() || isLoading || disabled) {
          e.preventDefault();
          // focus textarea
          textareaRef.current?.focus();
          return;
        }
        onSubmit(e);
      }}
      className="max-w-3xl lg:max-w-4xl xl:max-w-5xl lg:px-4 mx-auto"
    >
      {/* Context Indicator and Credit Display */}
      <div className="flex justify-between items-center px-4 mb-0.5">
        {/* Context Indicator on the left */}
        {contextIndicator ? (
          <div className="flex items-center gap-2 bg-gradient-to-bl from-[#00FFBF]/10 to-[#2E95FF]/10 dark:from-[#00FFBF]/5 dark:to-[#2E95FF]/5 px-3 py-3 rounded-lg ml-3 -mb-2">
            <span className="text-sm -mt-1">{contextIndicator.icon}</span>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 -mt-1">
              {contextIndicator.text}
            </span>
            {contextIndicator.subtext && (
              <span className="text-xs text-[#2E95FF] dark:text-[#00FFBF] ml-1">
                {contextIndicator.subtext}
              </span>
            )}
          </div>
        ) : (
          <div />
        )}

        {/* Credit Display on the right */}
        {selectedModel && userCreditsRemaining !== undefined ? (
          <CreditDisplay
            selectedModelId={selectedModel.id}
            userCreditsRemaining={userCreditsRemaining}
            userTier={userTier}
          />
        ) : (
          <div />
        )}
      </div>

      <div
        className={cn(
          'relative bg-white/95 dark:bg-[#000000a0] backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 px-5 py-3 shadow-lg dark:shadow-sm shadow-gray-400/20 dark:shadow-gray-600/10 transition-opacity cursor-text',
          isMultiline
            ? 'flex flex-col gap-2 rounded-2xl'
            : 'flex flex-row items-center gap-2 rounded-full',

          className
        )}
        onClick={handleContainerClick}
      >
        {isMultiline && (
          <div className="flex items-center justify-between w-full order-2">
            <ModelDropdown
              selectedModel={selectedModel}
              onModelSelect={setSelectedModel}
              className="-ml-1"
            />
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                type="submit"
                size="icon"
                disabled={isLoading}
                className={cn(
                  'rounded-full p-2 transition-all ml-3',
                  'bg-gradient-to-bl from-[#FF0090] to-[#7200DD] hover:from-[#FF0090]/90 hover:to-[#7200DD]/90 text-white shadow-md shadow-[#FF0090]/20 cursor-pointer'
                )}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        )}
        {!isMultiline && (
          <ModelDropdown
            selectedModel={selectedModel}
            onModelSelect={setSelectedModel}
            className="-ml-1.5 order-1"
          />
        )}
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={responsivePlaceholder}
          className={cn(
            'resize-none outline-none text-[15px] leading-relaxed max-h-[200px] bg-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 text-gray-900 dark:text-gray-100 min-h-[26px] overflow-y-auto',
            isMultiline ? 'w-full order-1' : 'flex-1 ml-2 order-2'
          )}
          rows={1}
        />
        {!isMultiline && (
          <div className="flex items-center gap-2 flex-shrink-0 order-3">
            {/* <Button
              type="button"
              variant="ghost"
              size="icon"
              className="hover:bg-gray-100 rounded-full p-2 group flex-shrink-0"
              disabled={isLoading || disabled}
            >
              <Paperclip className="h-5 w-5 text-gray-400 group-hover:text-[#2E95FF]" />
            </Button> */}

            <Button
              type="submit"
              size="icon"
              disabled={isLoading}
              className={cn(
                'rounded-full p-2 transition-all ml-3 -mr-1.5',
                'bg-gradient-to-bl from-[#FF0090] to-[#7200DD] hover:from-[#FF0090]/90 hover:to-[#7200DD]/90 text-white cursor-pointer'
              )}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
}
